import { Rank } from '../sim/defs';

/* ═══════════════════════════════════════════════════════════════════════════
   Les classes d'explosion (PLAN.md §4.7).

   Une explosion n'est pas « un gros système de particules » : c'est un empilement
   de TROIS COUCHES HÉTÉROGÈNES, chacune traitant ce qu'elle fait le mieux.

     1. le cœur turbulent      → planche pré-rendue (flipbook)
     2. les débris et étincelles → trajectoire analytique en vertex shader
     3. l'onde de choc          → effet écran, aucune géométrie

   Vouloir tout faire avec des particules coûte cher et rend mal : le volume
   turbulent est précisément ce que les particules simulent le plus mal.

   ⚠️ ÉTAT ACTUEL — la couche 1 est un PLACEHOLDER procédural. Les planches
   d'explosion n'existent en CC0 nulle part : il faut soit EmberGen, soit
   plusieurs jours de Blender Mantaflow, soit l'achat d'un pack VFX. C'est une
   décision de budget, pas de code (PLAN.md §10, question 4), et elle est
   reportée. Les couches 2 et 3 sont complètes ; brancher un vrai flipbook plus
   tard ne demandera de toucher qu'à `ExplosionFx.core()`.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface ExplosionClass {
  readonly name: string;
  /** Nombre de débris émis. */
  readonly particles: number;
  /** Durée du cœur, en secondes. */
  readonly duration: number;
  readonly speedMin: number;
  readonly speedMax: number;
  /** Rayon du cœur, en unités. */
  readonly coreRadius: number;
  /** Apport de trauma à la caméra. */
  readonly trauma: number;
  /** Lampes du pool demandées. Le pool en compte trois : au-delà, la demande
   *  est refusée en silence et l'explosion se contente de l'émissif. */
  readonly lights: number;
  readonly lightIntensity: number;
  readonly lightRadius: number;
  readonly lightDuration: number;
  /** Rayon final de l'onde de choc, en unités. 0 = pas d'onde. */
  readonly shockRadius: number;
  readonly shockDuration: number;
  /** Priorité d'attribution d'une lampe. Les grosses explosions passent devant. */
  readonly priority: number;
}

export const POPCORN: ExplosionClass = {
  name: 'popcorn',
  particles: 10,
  duration: 0.3,
  speedMin: 90,
  speedMax: 260,
  coreRadius: 26,
  trauma: 0.04,
  lights: 0,
  lightIntensity: 0,
  lightRadius: 0,
  lightDuration: 0,
  shockRadius: 0,
  shockDuration: 0,
  priority: 0,
};

export const STANDARD: ExplosionClass = {
  name: 'standard',
  particles: 26,
  duration: 0.48,
  speedMin: 120,
  speedMax: 380,
  coreRadius: 46,
  trauma: 0.11,
  lights: 0,
  lightIntensity: 0,
  lightRadius: 0,
  lightDuration: 0,
  shockRadius: 90,
  shockDuration: 0.26,
  priority: 1,
};

export const HEAVY: ExplosionClass = {
  name: 'lourd',
  particles: 60,
  duration: 0.85,
  speedMin: 160,
  speedMax: 520,
  coreRadius: 78,
  trauma: 0.26,
  lights: 1,
  lightIntensity: 9,
  lightRadius: 420,
  lightDuration: 0.2,
  shockRadius: 225,
  shockDuration: 0.42,
  priority: 2,
};

/** Réservée aux boss (J9). Présente ici pour que la table soit complète et que
 *  le budget de particules soit dimensionné dès maintenant. */
export const BOSS: ExplosionClass = {
  name: 'boss',
  particles: 300,
  duration: 3.2,
  speedMin: 200,
  speedMax: 700,
  coreRadius: 220,
  trauma: 0.85,
  lights: 3,
  lightIntensity: 14,
  lightRadius: 900,
  lightDuration: 0.5,
  shockRadius: 900,
  shockDuration: 1.1,
  priority: 5,
};

export const CLASSES: readonly ExplosionClass[] = [POPCORN, STANDARD, HEAVY, BOSS];

/** Choix de la classe à partir du RANG publié par la simulation.
 *
 *  La présentation ne connaît donc aucun archétype en particulier : ajouter un
 *  ennemi lui donne automatiquement l'explosion de sa catégorie. */
export function classForRank(rank: number): ExplosionClass {
  switch (rank) {
    case Rank.Popcorn:
      return POPCORN;
    case Rank.Heavy:
      return HEAVY;
    default:
      return STANDARD;
  }
}

/** Budget total de particules si les N plus grosses explosions du niveau
 *  survenaient en même temps. Sert à dimensionner le champ, et à vérifier en
 *  CI qu'on ne dépasse pas la capacité allouée. */
export function worstCaseParticles(simultaneous: number, cls: ExplosionClass): number {
  return simultaneous * cls.particles;
}
